import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  X, Buildings, Plus, PencilSimple, Power, UserCircle, ShieldCheck,
  ArrowsClockwise, Check, EyeSlash, Eye, Rocket,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Super Admin Panel — minimal, pilot-focused.
 * Lists organizations, allows create/edit/disable + per-org user management.
 * Visible only to super_admin (caller is responsible for gating the entry point).
 */
export default function SuperAdminPanel({ onClose, onEnrollPilot }) {
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOrg, setEditingOrg] = useState(null);
  const [creatingUserForOrg, setCreatingUserForOrg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [o, u] = await Promise.all([
        axios.get(`${API}/admin/organizations`),
        axios.get(`${API}/admin/users`),
      ]);
      setOrgs(o.data || []);
      setUsers(u.data || []);
    } catch (e) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const disableOrg = async (org) => {
    const next = (org.status === "active" || !org.status) ? "disabled" : "active";
    if (!window.confirm(`${next === "disabled" ? "Disable" : "Re-enable"} "${org.name}"? Users in this org ${next === "disabled" ? "will be locked out immediately." : "will regain access."}`)) return;
    try {
      await axios.put(`${API}/admin/organizations/${org.id}`, { status: next });
      toast.success(`${org.name} → ${next}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="fixed inset-0 z-[1900] bg-black/75 backdrop-blur-sm flex items-stretch justify-center"
      onClick={onClose}
      data-testid="super-admin-overlay">
      <div className="bg-zinc-950 border-l border-zinc-800 w-full max-w-5xl ml-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="super-admin-panel">

        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
              <ShieldCheck size={18} weight="duotone" className="text-violet-400" />
              Super Admin · Tenants
              <span className="text-[9px] uppercase tracking-wider font-bold text-violet-300 border border-violet-700/50 bg-violet-500/10 rounded px-1.5 py-0.5 ml-1">
                platform
              </span>
            </h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Manage organizations, assign users, disable tenants. Pilot-scoped.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={load} className="h-8 w-8 p-0" data-testid="super-admin-refresh"><ArrowsClockwise size={14} /></Button>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0"><X size={16} /></Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500">Organizations · {orgs.length}</div>
            <Button size="sm" onClick={() => setEditingOrg({})} className="h-7 text-xs bg-violet-600 hover:bg-violet-500" data-testid="create-org-btn">
              <Plus size={11} className="mr-1" /> New Organization
            </Button>
          </div>

          {loading ? (
            <div className="text-[11px] text-zinc-500 italic">Loading…</div>
          ) : (
            <div className="space-y-3">
              {orgs.map((org) => {
                const orgUsers = users.filter((u) => u.org_id === org.id);
                const disabled = org.status && org.status !== "active";
                return (
                  <div key={org.id}
                    className={`border ${disabled ? "border-red-900/40 bg-red-500/5" : "border-zinc-800 bg-zinc-900/40"} rounded p-3`}
                    data-testid={`org-row-${org.slug || org.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Buildings size={14} className="text-violet-400" weight="duotone" />
                          <span className="text-sm font-semibold text-zinc-100">{org.name}</span>
                          {org.plan && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">{org.plan}</span>}
                          {disabled && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-700/40">disabled</span>}
                          {org.tenant_type === "pilot" && (
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-700/40"
                              data-testid={`pilot-badge-${org.slug || org.id}`}>pilot</span>
                          )}
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">{org.id}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setCreatingUserForOrg(org)}
                          data-testid={`add-user-to-${org.slug || org.id}`}>
                          <Plus size={11} className="mr-1" /> User
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setEditingOrg(org)}>
                          <PencilSimple size={11} className="mr-1" /> Edit
                        </Button>
                        {org.tenant_type !== "pilot" && onEnrollPilot && (
                          <Button size="sm" variant="ghost"
                            className="h-7 text-[11px] text-teal-300"
                            onClick={() => onEnrollPilot(org.id)}
                            data-testid={`enroll-pilot-${org.slug || org.id}`}
                            title="Convert this production org into a pilot tenant">
                            <Rocket size={11} className="mr-1" /> Enroll as Pilot
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className={`h-7 text-[11px] ${disabled ? "text-emerald-300" : "text-amber-300"}`}
                          onClick={() => disableOrg(org)}
                          data-testid={`disable-${org.slug || org.id}`}>
                          <Power size={11} className="mr-1" /> {disabled ? "Re-enable" : "Disable"}
                        </Button>
                      </div>
                    </div>

                    {/* Users in this org */}
                    <div className="pl-5 mt-2 border-l border-zinc-800 space-y-1">
                      {orgUsers.length === 0 && (
                        <div className="text-[10px] italic text-zinc-600">No users assigned.</div>
                      )}
                      {orgUsers.map((u) => (
                        <div key={u._id} className="flex items-center gap-2 text-[11px]">
                          <UserCircle size={11} className="text-zinc-500" weight="duotone" />
                          <span className="text-zinc-200">{u.name || "(no name)"}</span>
                          <span className="text-zinc-500">· {u.email}</span>
                          <span className={`text-[9px] uppercase tracking-wider px-1 rounded ${
                            u.role === "super_admin" ? "bg-violet-500/15 text-violet-300" :
                            u.role === "org_admin"   ? "bg-teal-500/15 text-teal-300" :
                            "bg-zinc-800 text-zinc-400"
                          }`}>{u.role}</span>
                          {u.must_change_password && (
                            <span className="text-[9px] uppercase tracking-wider px-1 rounded bg-amber-500/15 text-amber-300" title="User has not yet rotated their initial password.">pwd reset pending</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editingOrg && (
        <OrgEditor org={editingOrg} onClose={() => setEditingOrg(null)} onSaved={() => { setEditingOrg(null); load(); }} />
      )}
      {creatingUserForOrg && (
        <UserCreator org={creatingUserForOrg} onClose={() => setCreatingUserForOrg(null)} onSaved={() => { setCreatingUserForOrg(null); load(); }} />
      )}
    </div>
  );
}

function OrgEditor({ org, onClose, onSaved }) {
  const isEdit = !!org.id;
  const [form, setForm] = useState({
    name: org.name || "",
    slug: org.slug || "",
    plan: org.plan || "standard",
    status: org.status || "active",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/organizations/${org.id}`, form);
      } else {
        await axios.post(`${API}/admin/organizations`, form);
      }
      toast.success(`Organization ${isEdit ? "updated" : "created"}`);
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[55] bg-black/70 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}
        data-testid="org-editor">
        <h3 className="text-base font-semibold text-zinc-100 mb-4">{isEdit ? "Edit" : "New"} Organization</h3>
        <div className="space-y-3">
          <Field label="Name *">
            <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="org-name-input" />
          </Field>
          <Field label="Slug (URL-safe)">
            <Input value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-")})}
              placeholder="rte-pilot" data-testid="org-slug-input" />
          </Field>
          <Field label="Plan">
            <select className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm rounded h-9 px-2"
              value={form.plan} onChange={(e) => setForm({...form, plan: e.target.value})} data-testid="org-plan-select">
              <option value="standard">standard</option>
              <option value="pilot">pilot</option>
              <option value="enterprise">enterprise</option>
            </select>
          </Field>
          {isEdit && (
            <Field label="Status">
              <select className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm rounded h-9 px-2"
                value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
                <option value="active">active</option>
                <option value="disabled">disabled</option>
              </select>
            </Field>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy || !form.name} className="bg-violet-600 hover:bg-violet-500"
            data-testid="save-org-btn">
            <Check size={11} className="mr-1" /> {isEdit ? "Save" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function UserCreator({ org, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "user", must_change_password: true,
  });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const gen = () => {
    const chars = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789@#$%";
    let s = "";
    for (let i = 0; i < 16; i++) s += chars[Math.floor(Math.random() * chars.length)];
    setForm({...form, password: s});
    setShow(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/users`, { ...form, org_id: org.id });
      toast.success(`${form.name} provisioned for ${org.name}`);
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Provisioning failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[55] bg-black/70 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}
        data-testid="user-creator">
        <h3 className="text-base font-semibold text-zinc-100 mb-1">New User</h3>
        <p className="text-[11px] text-zinc-500 mb-4">in <span className="text-zinc-300 font-semibold">{org.name}</span></p>
        <div className="space-y-3">
          <Field label="Full Name *">
            <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="user-name-input" />
          </Field>
          <Field label="Email *">
            <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="user-email-input" />
          </Field>
          <Field label="Role">
            <select className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm rounded h-9 px-2"
              value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} data-testid="user-role-select">
              <option value="viewer">viewer</option>
              <option value="user">user</option>
              <option value="org_admin">org_admin</option>
            </select>
          </Field>
          <Field label="Temporary Password *">
            <div className="flex gap-2">
              <Input type={show ? "text" : "password"} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
                placeholder="At least 8 characters" data-testid="user-temp-pwd-input" />
              <Button size="sm" variant="ghost" type="button" onClick={() => setShow(!show)} className="h-9 w-9 p-0">
                {show ? <EyeSlash size={13} /> : <Eye size={13} />}
              </Button>
              <Button size="sm" variant="outline" type="button" onClick={gen} className="h-9 text-[11px]">Generate</Button>
            </div>
          </Field>
          <label className="flex items-center gap-2 text-[11px] text-zinc-400">
            <input type="checkbox" checked={form.must_change_password}
              onChange={(e) => setForm({...form, must_change_password: e.target.checked})} />
            Force password change on first login
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={save}
            disabled={busy || !form.email || !form.name || (form.password || "").length < 8}
            className="bg-violet-600 hover:bg-violet-500" data-testid="save-user-btn">
            <Check size={11} className="mr-1" /> Provision User
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
